import React, { useContext, useState, Fragment } from 'react';
import { IoMdClose } from 'react-icons/io';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';

import { OrderContext } from '../../context/orderContext';

import ActionButton from '../../components/helpers/ActionButton';
import { formatDollar } from '../../utils/CurrencyFormatter';
import { DownloadIcon } from '../../components/Icons';
import { Box, Flex, Avatar, Heading, Text } from '@chakra-ui/core';

const OrderCard = ({
  percentage,
  status,
  order,
  setDeletedOrder,
  token,
  firstName,
  session,
  setSession,
  setUnexpectedError
}) => {
  const { deleteOrder, downloadPDF } = useContext(OrderContext);
  const [isSubmittingA, setIsSubmittingA] = useState(false);
  const [isSubmittingB, setIsSubmittingB] = useState(false);

  return (
    <Box className="pt-8 pb-16">
      <Box mx="auto" className="container">
        <Flex wrap="wrap" align="center" justify="center" mt={1}>
          <Box w={{ base: '100%', md: 4 / 5 }} bg="white" rounded="sm" p={8} shadow="lg" mb={5}>
            <Box
              d={{ base: 'block', md: 'flex' }}
              flexWrap={{ md: 'wrap' }}
              justifyContent={{ md: 'space-between' }}
              alignItems={{ md: 'center' }}
            >
              <Flex justify="space-between">
                <Avatar
                  src={order.imageUrl}
                  alt={order.crop}
                  h={{ base: 16, md: 24 }}
                  w={{ base: 16, md: 24 }}
                />
                <Box ml={{ md: 4 }}>
                  <Heading as="h6">{order.crop}</Heading>
                  <Text as="ul" fontSize="sm">
                    <Text as="li">Order Reference: {order.refKey}</Text>
                    <Text as="li">{order.acreage} Acre(s) | Farm Insured</Text>
                    <Text as="li">{formatDollar(order.cost)}</Text>
                  </Text>
                </Box>
              </Flex>
              <Box textAlign="center" mt={{ base: 6, md: 0 }}>
                <Text as="span" textAlign="right" fontWeight="bold" mb={2}>
                  {percentage}% Complete
                </Text>
                <Flex justify="flex-end">
                  {status === 'completed' ? (
                    <Fragment>
                      {order.receipt ? (
                        <a href={order.receipt} download>
                          <ActionButton
                            name="Receipt"
                            color="bg-cf-300"
                            textColor="text-white"
                            icon={
                              <DownloadIcon
                                styles={{
                                  margin: '2px 5px 0 0',
                                  size: '12px',
                                  verticalAlign: 'middle'
                                }}
                              />
                            }
                            btnPad={'px-4 py-3'}
                            className="mr-1"
                          />
                        </a>
                      ) : (
                        <ActionButton
                          isSubmitting={isSubmittingA}
                          name="Receipt"
                          color="bg-cf-300"
                          textColor="text-white"
                          icon={
                            <DownloadIcon
                              styles={{
                                margin: '2px 5px 0 0',
                                size: '12px',
                                verticalAlign: 'middle'
                              }}
                            />
                          }
                          click={async () => {
                            try {
                              setIsSubmittingA(true);
                              const response = await downloadPDF(order._id, 'receipt', token);
                              setIsSubmittingA(false);
                              if (response.status === 200) {
                                const pdfBlob = new Blob([response.data], {
                                  type: 'application/pdf'
                                });
                                saveAs(pdfBlob, `${order.refKey}-receipt-for-${firstName}.pdf`);
                                toast.success('Download Completed', {
                                  position: toast.POSITION.TOP_RIGHT
                                });
                              } else {
                                toast.error('Failed to download, Retry!', {
                                  position: toast.POSITION.TOP_RIGHT
                                });
                              }
                            } catch (error) {
                              if (error && error.status === 401) setSession(false);
                              else setUnexpectedError('Unexpected Server Error!');
                            }
                          }}
                          btnPad={'px-4 py-3'}
                          className="mr-1"
                        />
                      )}
                    </Fragment>
                  ) : (
                    <Fragment>
                      {order.invoice ? (
                        <a href={order.invoice} download>
                          <ActionButton
                            name="Invoice"
                            color="bg-cf-300"
                            textColor="text-white"
                            icon={
                              <DownloadIcon
                                styles={{
                                  margin: '2px 5px 0 0',
                                  size: '12px',
                                  verticalAlign: 'middle'
                                }}
                              />
                            }
                            btnPad={'px-4 py-3'}
                            className="mr-1"
                          />
                        </a>
                      ) : (
                        <ActionButton
                          isSubmitting={isSubmittingA}
                          name="Invoice"
                          color="bg-cf-300"
                          textColor="text-white"
                          icon={
                            <DownloadIcon
                              styles={{
                                margin: '2px 5px 0 0',
                                size: '12px',
                                verticalAlign: 'middle'
                              }}
                            />
                          }
                          click={async () => {
                            if (session)
                              try {
                                setIsSubmittingA(true);
                                const response = await downloadPDF(order._id, 'invoice', token);
                                setIsSubmittingA(false);
                                if (response.status === 200) {
                                  const pdfBlob = new Blob([response.data], {
                                    type: 'application/pdf'
                                  });
                                  saveAs(pdfBlob, `${order.refKey}-invoice-for-${firstName}.pdf`);
                                  toast.success('Download Completed', {
                                    position: toast.POSITION.TOP_RIGHT
                                  });
                                } else {
                                  toast.error('Failed to download, Retry!', {
                                    position: toast.POSITION.TOP_RIGHT
                                  });
                                }
                              } catch (error) {
                                if (error && error.status === 401) setSession(false);
                                else setUnexpectedError('Unexpected Server Error!');
                              }
                          }}
                          btnPad={'px-4 py-3'}
                          className="mr-1"
                        />
                      )}
                      {!order.processingPayment && (
                        <ActionButton
                          isSubmitting={isSubmittingB}
                          color="bg-red-200"
                          textColor="text-red-600"
                          name="Cancel"
                          icon={<IoMdClose size={20} />}
                          click={async () => {
                            if (session)
                              try {
                                setIsSubmittingB(true);
                                const response = await deleteOrder(order._id);
                                setIsSubmittingB(false);
                                if (response.code === 200) {
                                  setDeletedOrder(response.data);
                                  toast.success(response.message, {
                                    position: toast.POSITION.TOP_RIGHT
                                  });
                                  // toast.info('You can revert this now or never!', {
                                  //   position: toast.POSITION.TOP_RIGHT
                                  // });
                                } else {
                                  toast.error(response.data.message, {
                                    position: toast.POSITION.TOP_RIGHT
                                  });
                                }
                              } catch (error) {
                                console.log(error);
                                if (error && error.status === 401) setSession(false);
                                else setUnexpectedError('Unexpected Server Error!');
                              }
                          }}
                          btnPad={'px-4 py-3'}
                        />
                      )}
                    </Fragment>
                  )}
                </Flex>
              </Box>
            </Box>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default OrderCard;
